import React from 'react';
import { useTalent } from "../../../providers/TalentProvider";
import ProfileContainer from "../components/ProfileContainer";
import CircleProgress from "../../../components/CircleProgress";
import { ResumeDisciplineList } from "./hire-confidence/HireConfidenceBreakdowns";
import { AiDiamondIcon, THEME_COLORS } from "./hire-confidence/HireConfidenceParts";

/**
 * The AI's read of the talent's resume, on its own.
 *
 * This is the same list the Role Match breakdown carries inside Hire
 * Confidence, deliberately repeated here — because Hire Confidence doesn't
 * exist until a candidate has proposed and been assessed, and the decision a
 * client lead makes before that is whether to invite them at all. Burying the
 * one AI read that *is* available behind a score that isn't left the earliest
 * decision as the least informed one.
 *
 * Internal-only, like Match Score and Hire Confidence: gated to
 * `canManageClients()` in TalentProfilePage.
 */
const TalentProfileResumeScore = () => {
    const { talent } = useTalent();

    const score = talent?.resumeMatchScore?.();
    const disciplines = talent?.scoredDisciplines?.() || [];

    if (score === null || score === undefined) {
        return null;
    }

    return (
        <div id="ai-resume-score" className="pt-2 pb-8">
            <ProfileContainer>

                <div className="flex flex-col gap-5 border-b border-[#EDEEF2] pb-6 sm:flex-row sm:items-center sm:gap-6">
                    <div className="h-[120px] w-[120px] shrink-0">
                        <CircleProgress
                            progress={talent.resumeMatchScore(false)}
                            strokeWidth={8}
                            pathColor={THEME_COLORS.primary}
                            trailColor="#EFEFF3"
                        >
                            <p className="m-0">
                                <span className="text-3xl">{Math.round(score)}</span>
                            </p>
                        </CircleProgress>
                    </div>

                    <div className="min-w-0">
                        <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1">
                            <p className="mb-0 flex flex-row items-center gap-2 text-3xl font-bold">
                                <AiDiamondIcon className="h-5 w-5 shrink-0 text-primary" />
                                AI Resume Score
                            </p>
                            <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.09em] text-white">
                                Beta
                            </span>
                        </div>

                        {/* Says plainly what this is and isn't. It reads the
                            resume against the role's matched disciplines, which
                            is a different question from the profile-against-role
                            Match Score above — and a reviewer who conflates the
                            two will over-read a low number here. */}
                        <p className="mb-0 mt-2 text-sm leading-[1.55] text-[#595D68]">
                            {disciplines.length > 1
                                ? `The AI’s read of this talent’s resume, scored against each of the ${disciplines.length} matched roles and averaged. `
                                : 'The AI’s read of this talent’s resume, scored against the matched role. '}
                            Available from the moment they match, unlike Hire Confidence —
                            which needs a submitted proposal before it can be scored at all.
                        </p>

                        <p className="mb-0 mt-2 text-xs leading-[1.5] text-[#8B8F9A]">
                            Feeds half of Role Match, and so 22.5 of the 100 points behind
                            Hire Confidence, once that score exists.
                        </p>
                    </div>
                </div>

                <div className="mt-5">
                    <p className="mb-3 text-sm font-bold text-[#0E0E14]">
                        {TB.pluralize('Matched role', disciplines.length, true)}
                    </p>

                    <ResumeDisciplineList
                        disciplines={disciplines}
                        emptyChildren={(
                            <>
                                This talent has a resume score, but none of their matched
                                disciplines carry a per-role analysis yet — so there is
                                nothing here to open.
                            </>
                        )}
                    />
                </div>

            </ProfileContainer>
        </div>
    );
};

export default TalentProfileResumeScore;
